/*
 * CallingWorkspace.jsx — the SA-facing + Manager-facing pages introduced
 * in the Feb 2026 pivot (Loan CRM becomes the single system).
 *
 * Exports
 *   - CallingDataTab       : SA's own calling queue + inline call modal
 *   - MyFollowupsTab       : SA's follow-ups grouped by bucket
 *   - ManagerTeamDashboard : per-SA funnel for a Sales Manager's team
 *   - PerformanceReports   : Calling / Follow-up / Login / Approval / Disbursement
 */
import { useEffect, useMemo, useRef, useState } from "react";
import {
  Phone, PhoneCall, PhoneOff, PhoneOutgoing, Eye, EyeOff, CalendarClock,
  CheckCircle2, XCircle, Send, Loader2, Users, AlertTriangle,
  ChevronLeft, ClipboardList, FileText, Calculator, Megaphone, Clock, UserPlus,
  History, Building2,
} from "lucide-react";
import { http, money, notify, maskMobile } from "../api";
import { Loading, Modal } from "./common";
import { LeadForm } from "./forms";

const FALLBACK_DISPOSITIONS = [
  "Called", "Connected", "Not Connected", "Interested", "Not Interested",
  "Ringing", "Busy", "Wrong Number", "Follow Up",
  "Callback", "Switched Off", "Call Later",
  "Create Lead",
];

// Case-insensitive detection for the "route to Lead Creation form" trigger.
const isCreateLeadDispo = (label) => {
  const s = String(label || "").toLowerCase();
  return s.includes("create lead");
};

// Resolve the ONE effective status label a customer should show. It is
// bucket-aware: whatever bucket the SA has selected in the filter must be
// what the badge says (so a customer visible in "Lead Created" shows
// LEAD CREATED even if they also have a callback scheduled). In the "All"
// bucket we fall back to a natural priority ladder.
const effectiveStatusLabel = (c, bucket = "") => {
  if (!c) return "";
  // Bucket-forced labels — the badge MUST agree with the filter that
  // surfaced this row (otherwise the label looks like it belongs to a
  // different section, which confuses the SA).
  if (bucket === "sent_to_login") return "SENT TO LOGIN";
  if (bucket === "skipped") return "SKIPPED";
  if (bucket === "callback") return "CALLBACK";
  if (bucket === "follow_up") return "FOLLOW UP";
  if (bucket === "lead_created") return "LEAD CREATED";
  // "All" / no filter — resolve by state (highest wins).
  if (c.status === "sent_to_login") return "SENT TO LOGIN";
  if (c.status === "skipped") return "SKIPPED";
  if (c.next_callback_at) return "CALLBACK";
  if (c.linked_lead_id) return "LEAD CREATED";
  return String(c.last_disposition || "").toUpperCase();
};

// Tone for the status badge — greens for progress, amber for pending action.
const statusTone = (label) => {
  const u = (label || "").toUpperCase();
  if (u === "SENT TO LOGIN") return "green";
  if (u === "LEAD CREATED") return "green";
  if (u === "SKIPPED") return "red";
  if (u === "NOT INTERESTED" || u === "WRONG NUMBER") return "red";
  return "amber";
};

/* ================================================================== */
/*  1. CALLING DATA TAB (Sales Associate)                              */
/*     - Landing = card-based Home ("Calling Data Overview")           */
/*     - Drills into a filtered list of campaign_customers             */
/* ================================================================== */
export function CallingDataTab({ user, onNavigate, onOpenLead }) {
  const [mode, setMode] = useState("home"); // home | list
  const [listStatus, setListStatus] = useState("assigned");
  const [overview, setOverview] = useState(null);
  const [scriptsOpen, setScriptsOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  // Pre-filled Create-Lead flow (bubbled up from CustomerCallModal)
  const [leadPrefill, setLeadPrefill] = useState(null);
  // Mobile-only: standalone Create-Lead button on the Overview.
  const [mobileCreateLead, setMobileCreateLead] = useState(false);
  const [listRefreshTick, setListRefreshTick] = useState(0);
  // Multi-campaign selector — persists across tab switches so the SA doesn't
  // have to pick their campaign again when hopping between buckets.
  const [availableCamps, setAvailableCamps] = useState([]);
  const [selCampaign, setSelCampaign] = useState("");
  const [selDate, setSelDate] = useState("");

  useEffect(() => {
    http.get("/sa/available-campaigns")
      .then((r) => setAvailableCamps(r.data.campaigns || []))
      .catch(() => setAvailableCamps([]));
  }, []);

  const dateOptions = (() => {
    const dates = new Set();
    for (const c of availableCamps) {
      if (!selCampaign || c.campaign_id === selCampaign) {
        if (c.campaign_date) dates.add(c.campaign_date);
      }
    }
    return Array.from(dates).sort().reverse();
  })();

  const loadOverview = () => {
    const qs = new URLSearchParams();
    if (selCampaign) qs.set("campaign_id", selCampaign);
    if (selDate) qs.set("campaign_date", selDate);
    const url = qs.toString() ? `/sa/calling-overview?${qs}` : "/sa/calling-overview";
    http.get(url)
      .then((r) => setOverview(r.data))
      .catch((err) => notify.error(err, "Failed to load overview"));
  };
  useEffect(() => { if (mode === "home") loadOverview(); }, [mode, selCampaign, selDate]);

  const openBucket = (status) => {
    setListStatus(status);
    setMode("list");
  };

  if (mode === "list") {
    return (
      <>
        <CallingDataList
          user={user}
          status={listStatus}
          refreshTick={listRefreshTick}
          campaignId={selCampaign}
          campaignDate={selDate}
          onBack={() => setMode("home")}
          onCreateLead={(cc) => setLeadPrefill(cc)}
          onOpenLead={onOpenLead}
        />
        {leadPrefill && (
          <PrefilledLeadForm
            customer={leadPrefill}
            user={user}
            onClose={() => setLeadPrefill(null)}
            onDone={(createdLeadId) => {
              setLeadPrefill(null);
              setListRefreshTick((n) => n + 1);
              // STRICT WORKFLOW: after Create Lead, auto-open the lead
              // detail in Review/Edit mode so the SA reviews every field
              // (Mobile is read-only) BEFORE clicking Send to Login.
              if (createdLeadId && onOpenLead) onOpenLead(createdLeadId);
            }}
          />
        )}
      </>
    );
  }

  return (
    <section data-testid="calling-workspace-home">
      {availableCamps.length > 0 && (
        <div
          className="campaign-selector"
          data-testid="campaign-selector-bar"
          style={{ display: "flex", flexWrap: "wrap", gap: 12, alignItems: "center",
                   padding: "10px 14px", marginBottom: 12,
                   background: "var(--surface)", border: "1px solid var(--border)",
                   borderRadius: 8 }}
        >
          <label style={{ display: "flex", alignItems: "center", gap: 6, margin: 0 }}>
            <span className="muted" style={{ fontSize: 12 }}>Campaign</span>
            <select
              data-testid="sa-campaign-select"
              value={selCampaign}
              onChange={(e) => { setSelCampaign(e.target.value); setSelDate(""); }}
              style={{ padding: "8px 12px" }}
            >
              <option value="">All Campaigns</option>
              {availableCamps.map((c) => (
                <option key={c.campaign_id} value={c.campaign_id}>
                  {c.campaign_id} · {c.name}
                </option>
              ))}
            </select>
          </label>
          {dateOptions.length > 0 && (
            <label style={{ display: "flex", alignItems: "center", gap: 6, margin: 0 }}>
              <span className="muted" style={{ fontSize: 12 }}>Date</span>
              <select
                data-testid="sa-campaign-date-select"
                value={selDate}
                onChange={(e) => setSelDate(e.target.value)}
                style={{ padding: "8px 12px" }}
              >
                <option value="">All Dates</option>
                {dateOptions.map((d) => (
                  <option key={d} value={d}>{d}</option>
                ))}
              </select>
            </label>
          )}
          {(selCampaign || selDate) && (
            <button
              type="button"
              className="outline-button compact"
              data-testid="sa-campaign-clear"
              onClick={() => { setSelCampaign(""); setSelDate(""); }}
            >Clear</button>
          )}
        </div>
      )}
      <CallingWorkspaceHome
        overview={overview}
        onOpenBucket={openBucket}
        onOpenScripts={() => setScriptsOpen(true)}
        onOpenHistory={() => setHistoryOpen(true)}
        onNavigate={onNavigate}
        onCreateLead={() => setMobileCreateLead(true)}
      />
      {scriptsOpen && <ScriptsModal onClose={() => setScriptsOpen(false)} />}
      {historyOpen && <CallingHistoryModal onClose={() => setHistoryOpen(false)} />}
      {mobileCreateLead && (
        <LeadForm
          user={user}
          onClose={() => setMobileCreateLead(false)}
          onSaved={(savedLead) => {
            setMobileCreateLead(false);
            loadOverview();
            // Same auto-open behaviour as the desktop + New lead button.
            if (savedLead?.lead_id && onOpenLead) onOpenLead(savedLead.lead_id);
          }}
        />
      )}
    </section>
  );
}


/* ------------------------------------------------------------------ */
/*  Two-letter initials avatar used by the mobile card lists          */
/* ------------------------------------------------------------------ */
function initialsOf(name) {
  if (!name) return "?";
  const parts = String(name).trim().split(/\s+/).slice(0, 2);
  return parts.map((p) => p[0] || "").join("").toUpperCase() || "?";
}


/* ------------------------------------------------------------------ */
/*  Card-based home: (mobile-only Create Lead) + tile grid            */
/*  The 3 large KPI cards are hidden on mobile via CSS.               */
/* ------------------------------------------------------------------ */
export function CallingWorkspaceHome({ overview, onOpenBucket, onOpenScripts, onOpenHistory, onNavigate, onCreateLead, hideCreateLead = false, title = "Calling Data Overview", eyebrow = "SALES ASSOCIATE", subtitle }) {
  const kpis = [
    { key: "total_calls_today", label: "Total calls today", value: overview?.total_calls_today ?? "—", icon: PhoneCall, tone: "green" },
    { key: "followups", label: "Follow-ups", value: overview?.followups ?? "—", icon: Clock, tone: "amber" },
    { key: "sent_to_login_month", label: "Send to login", value: overview?.sent_to_login_month ?? "—", icon: Send, tone: "blue" },
  ];
  const tiles = [
    // Funnel row — read left-to-right for the customer's journey.
    { key: "assigned",         label: "Assigned Data",    value: overview?.assigned ?? 0,     icon: Users,         onClick: () => onOpenBucket("assigned") },
    { key: "followups",        label: "My Follow-ups",    value: overview?.followups ?? 0,    icon: Clock,         onClick: () => onNavigate?.("my_followups") },
    { key: "lead_created",     label: "Lead Created",     value: overview?.lead_created ?? 0, icon: UserPlus,      onClick: () => onOpenBucket("lead_created") },
    { key: "sent_to_login",    label: "Sent to Login",    value: overview?.sent_to_login_month ?? 0, icon: Send,   onClick: () => onOpenBucket("sent_to_login") },
    // Support row — secondary actions & tools.
    { key: "callback",         label: "Call Back",        value: overview?.callback ?? 0,     icon: PhoneOutgoing, onClick: () => onOpenBucket("callback") },
    { key: "pending",          label: "Pending Data",     value: overview?.pending ?? 0,      icon: AlertTriangle, onClick: () => onOpenBucket("pending") },
    { key: "calling_history",  label: "Calling History",  value: overview?.history ?? 0,      icon: History,       onClick: onOpenHistory },
    { key: "scripts",          label: "Calling Scripts",  value: overview?.scripts ?? 0,      icon: FileText,      onClick: onOpenScripts },
    { key: "emi",              label: "EMI Calculator",   value: "",                          icon: Calculator,    onClick: () => onNavigate?.("policy_finder") },
    { key: "bank_policy",      label: "Bank Policy",      value: "",                          icon: Building2,     onClick: () => onNavigate?.("policy_finder") },
    { key: "campaigns",        label: "Campaign Data",    value: overview?.campaigns ?? 0,    icon: Megaphone,     onClick: () => onOpenBucket("") },
  ];

  return (
    <>
      <div className="page-intro">
        <div>
          <p className="eyebrow">{eyebrow}</p>
          <h1>{title}</h1>
          <p className="muted" style={{ marginTop: 6 }}>
            {subtitle || "Everything you need to run your day — assigned lists, callbacks, follow-ups and scripts, one tap away."}
          </p>
        </div>
      </div>

      {/* Mobile-only: prominent Create-Lead action bar. Hidden on desktop, and
          also hidden when the report is rendered for Admin / Sales Manager. */}
      {!hideCreateLead && (
        <div className="mobile-create-lead-bar">
          <button
            type="button"
            data-testid="mobile-create-lead-button"
            onClick={onCreateLead}
          >
            <UserPlus size={16} /> Create Lead
          </button>
        </div>
      )}

      <div className="calling-kpi-grid" data-testid="calling-kpi-grid">
        {kpis.map(({ key, label, value, icon: Icon, tone }) => (
          <div key={key} className="calling-kpi" data-testid={`kpi-${key}`}>
            <div>
              <p>{label}</p>
              <strong>{value}</strong>
            </div>
            <span className={`calling-kpi-icon ${tone}`}><Icon size={22} /></span>
          </div>
        ))}
      </div>

      <div className="calling-tile-grid" data-testid="calling-tile-grid">
        {tiles.map(({ key, label, value, icon: Icon, onClick }) => (
          <button
            key={key}
            type="button"
            className="calling-tile"
            data-testid={`tile-${key}`}
            onClick={onClick}
          >
            <span className="calling-tile-icon"><Icon size={18} /></span>
            <div className="calling-tile-body">
              <strong>{label}</strong>
              {value !== "" && value !== null && value !== undefined && (
                <span className="calling-tile-count">{value}</span>
              )}
            </div>
          </button>
        ))}
      </div>
    </>
  );
}


/* ------------------------------------------------------------------ */
/*  Filtered list of campaign_customers (opens from a tile)           */
/* ------------------------------------------------------------------ */
function CallingDataList({ user, status, refreshTick, campaignId = "", campaignDate = "", onBack, onCreateLead, onOpenLead }) {
  const [rows, setRows] = useState(null);
  const [selected, setSelected] = useState(null);
  const [statusFilter, setStatusFilter] = useState(status);
  // STRICT CALL SEQUENCE: once an SA opens a customer's detail, they can't
  // open any other customer's number/details until this call's disposition
  // has been saved. `lockedCustomerId` holds the id of that in-progress
  // call; it's cleared on successful Save (via onChanged) or explicit unlock.
  const [lockedCustomerId, setLockedCustomerId] = useState(null);

  // Skipped Lead bucket sometimes returns synthetic rows (pending-review
  // leads without a campaign_customer). Their id is `lead::LD-...` — those
  // must open the full Lead sheet directly, not the Call modal (there's no
  // matching cc to fetch).
  const openRow = (c) => {
    const cid = c?.customer_id || "";
    // GATE: if another call is still in progress, block ALL detail access.
    if (lockedCustomerId && lockedCustomerId !== cid) {
      notify.error("Please complete and save the disposition for the current call before starting another.");
      return;
    }
    if (cid.startsWith("lead::") && c.linked_lead_id && onOpenLead) {
      onOpenLead(c.linked_lead_id);
    } else {
      setLockedCustomerId(cid);
      setSelected(cid);
    }
  };

  const reload = () => {
    setRows(null);
    const qs = new URLSearchParams({ status: statusFilter });
    if (campaignId) qs.set("campaign_id", campaignId);
    if (campaignDate) qs.set("campaign_date", campaignDate);
    http.get(`/sa/calling-data?${qs}`)
      .then((r) => setRows(r.data.customers || []))
      .catch((err) => notify.error(err, "Failed to load calling data"));
  };
  useEffect(reload, [statusFilter, refreshTick, campaignId, campaignDate]);

  const heading = {
    assigned: "Assigned Data",
    pending: "Pending Data",
    callback: "Call Back",
    follow_up: "Follow-Up (disposition)",
    lead_created: "Lead Created",
    completed: "Completed",
    sent_to_login: "Sent to Login",
    skipped: "Skipped Lead",
    "": "All calling data",
  }[statusFilter] || "Calling Data";

  return (
    <section className="panel" data-testid="calling-data-tab">
      <div className="section-title">
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <button
            type="button"
            className="back-button"
            data-testid="calling-list-back"
            style={{ margin: 0 }}
            onClick={onBack}
          ><ChevronLeft size={14} /> Overview</button>
          <h3 style={{ margin: 0 }}>{heading}</h3>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <select
            data-testid="calling-status-filter"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="assigned">Assigned (today)</option>
            <option value="pending">Pending (older)</option>
            <option value="callback">Call Back</option>
            <option value="follow_up">Follow-Up (disposition)</option>
            <option value="lead_created">Lead Created</option>
            <option value="">All</option>
            <option value="completed">Completed</option>
            <option value="sent_to_login">Sent to Login</option>
            <option value="skipped">Skipped Lead</option>
          </select>
          <span className="muted">{rows?.length ?? 0} customers</span>
        </div>
      </div>

      {rows === null ? <Loading /> : rows.length === 0 ? (
        <div className="empty" style={{ padding: 24 }}>
          No customers in this bucket.
        </div>
      ) : (
        <>
          {/* Desktop / tablet: dense table */}
          <div className="table-wrap desktop-list-only">
            <table className="data-table" data-testid="calling-data-table">
              <thead>
                <tr>
                  <th>Customer</th>
                  <th>Mobile</th>
                  <th>Campaign</th>
                  <th>Loan</th>
                  <th>Last call</th>
                  <th>Next follow-up</th>
                  <th>Status</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {rows.map((c) => (
                  <tr
                    key={c.customer_id}
                    data-testid={`cust-row-${c.customer_id}`}
                    onClick={() => openRow(c)}
                    style={{ cursor: "pointer" }}
                  >
                    <td>
                      <strong>{c.customer_name}</strong>
                      <div className="cell-sub">{c.city}</div>
                    </td>
                    <td>{c.mobile /* already masked by server */}</td>
                    <td className="cell-sub">{c.campaign_id}</td>
                    <td>
                      {c.loan_type}
                      {c.loan_amount ? <div className="cell-sub">{money(c.loan_amount)}</div> : null}
                    </td>
                    <td>
                      {c.last_disposition ? (
                        <>
                          <strong>{c.last_disposition}</strong>
                          <div className="cell-sub">{(c.last_disposition_at || "").slice(0, 16).replace("T", " ")}</div>
                        </>
                      ) : <span className="muted">—</span>}
                    </td>
                    <td>{c.next_followup_at ? c.next_followup_at.slice(0, 16).replace("T", " ") : <span className="muted">—</span>}</td>
                    <td>
                      {effectiveStatusLabel(c, statusFilter) && (
                        <span
                          className={`mc-tag ${statusTone(effectiveStatusLabel(c, statusFilter))}`}
                          data-testid={`cust-status-${c.customer_id}`}
                        >
                          {effectiveStatusLabel(c, statusFilter)}
                        </span>
                      )}
                    </td>
                    <td>
                      <button
                        className="primary-button compact"
                        data-testid={`desktop-call-${c.customer_id}`}
                        onClick={async (e) => {
                          e.stopPropagation();
                          if (lockedCustomerId && lockedCustomerId !== c.customer_id) {
                            notify.error("Please complete and save the disposition for the current call before starting another.");
                            return;
                          }
                          try {
                            const r = await http.get(`/sa/calling-data/${c.customer_id}`);
                            const raw = r.data?.customer?.mobile;
                            if (raw) {
                              setLockedCustomerId(c.customer_id);
                              window.location.href = `tel:${raw}`;
                            }
                          } catch (err) { notify.error(err, "Could not dial"); }
                        }}
                      >
                        <PhoneCall size={13} /> Call
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile: card list matching the design brief */}
          <ul className="mobile-card-list mobile-list-only" data-testid="calling-data-cards">
            {rows.map((c) => (
              <li
                key={c.customer_id}
                className="mc-row"
                data-testid={`cust-card-${c.customer_id}`}
              >
                <button
                  type="button"
                  className="mc-open"
                  data-testid={`open-details-${c.customer_id}`}
                  onClick={() => openRow(c)}
                >
                  <span className="mc-avatar">{initialsOf(c.customer_name)}</span>
                  <div className="mc-body">
                    <strong>{c.customer_name}</strong>
                    <span className="mc-mobile">{c.mobile}</span>
                    <span className="mc-loan">
                      {c.loan_type}
                      {c.loan_amount ? ` · ${money(c.loan_amount)}` : ""}
                    </span>
                    {effectiveStatusLabel(c, statusFilter) && (
                      <span
                        className={`mc-tag ${statusTone(effectiveStatusLabel(c, statusFilter))}`}
                        data-testid={`cust-status-${c.customer_id}`}
                      >
                        {effectiveStatusLabel(c, statusFilter)}
                      </span>
                    )}
                  </div>
                </button>
                <button
                  className="mc-call"
                  data-testid={`open-call-${c.customer_id}`}
                  onClick={async (e) => {
                    e.stopPropagation();
                    if (lockedCustomerId && lockedCustomerId !== c.customer_id) {
                      notify.error("Please complete and save the disposition for the current call before starting another.");
                      return;
                    }
                    try {
                      const r = await http.get(`/sa/calling-data/${c.customer_id}`);
                      const raw = r.data?.customer?.mobile;
                      if (raw) {
                        setLockedCustomerId(c.customer_id);
                        window.location.href = `tel:${raw}`;
                      }
                    } catch (err) { notify.error(err, "Could not dial"); }
                  }}
                >
                  <PhoneCall size={14} /> Call
                </button>
              </li>
            ))}
          </ul>
        </>
      )}

      {selected && (
        <CustomerCallModal
          customerId={selected}
          onClose={() => {
            // Unlock only if the customer already had a saved disposition
            // or a linked lead — otherwise the SA must first Save.
            const cur = (rows || []).find((r) => r.customer_id === selected);
            if (cur && (cur.last_disposition || cur.linked_lead_id)) {
              setLockedCustomerId(null);
            }
            setSelected(null);
          }}
          onChanged={() => { setLockedCustomerId(null); reload(); }}
          onCreateLead={(cc) => { setSelected(null); setLockedCustomerId(null); onCreateLead?.(cc); }}
          onNext={(currentId) => {
            // Pick the next customer in the currently-loaded list. If the
            // list already reflects the just-saved disposition (reload
            // above), the current row may already be gone — otherwise skip
            // it explicitly.
            const list = rows || [];
            const idx = list.findIndex((r) => r.customer_id === currentId);
            let next = null;
            if (idx >= 0) next = list.slice(idx + 1).find((r) => r.customer_id !== currentId);
            if (!next) next = list.find((r) => r.customer_id !== currentId);
            if (next) {
              setLockedCustomerId(next.customer_id);
              setSelected(next.customer_id);
            } else {
              notify.success("No more customers in this bucket — great job!");
            }
          }}
        />
      )}
    </section>
  );
}


/* ------------------------------------------------------------------ */
/*  Scripts modal (read-only) — shown from the Calling Scripts tile   */
/* ------------------------------------------------------------------ */
function ScriptsModal({ onClose }) {
  const [scripts, setScripts] = useState(null);
  const [selected, setSelected] = useState(null);
  useEffect(() => {
    http.get("/sa/scripts")
      .then((r) => setScripts(r.data.scripts || []))
      .catch((err) => notify.error(err, "Failed to load scripts"));
  }, []);
  return (
    <Modal title="Calling Scripts" onClose={onClose} wide>
      {scripts === null ? <Loading /> : scripts.length === 0 ? (
        <div className="empty" style={{ padding: 24 }} data-testid="scripts-empty">
          No active scripts yet — ask an admin to add them under Settings → Scripts.
        </div>
      ) : selected ? (
        <div data-testid="script-detail">
          <button type="button" className="back-button" onClick={() => setSelected(null)}>
            <ChevronLeft size={14} /> All scripts
          </button>
          <h3 style={{ margin: "6px 0" }}>{selected.title}</h3>
          {selected.loan_type && (
            <span className="chip chip-blue" style={{ fontSize: 11 }}>{selected.loan_type}</span>
          )}
          <pre className="script-body" data-testid="script-body">{selected.body}</pre>
        </div>
      ) : (
        <ul className="script-list" data-testid="script-list">
          {scripts.map((s) => (
            <li key={s.script_id}>
              <button
                type="button"
                data-testid={`script-item-${s.script_id}`}
                onClick={() => setSelected(s)}
              >
                <div>
                  <strong>{s.title}</strong>
                  {s.loan_type ? <small className="muted"> · {s.loan_type}</small> : null}
                </div>
                <span className="muted" style={{ fontSize: 11 }}>Open</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </Modal>
  );
}


/* ------------------------------------------------------------------ */
/*  Calling History modal — SA's last-7-day disposition log           */
/* ------------------------------------------------------------------ */
const DISPO_TONE = {
  Interested:      "green",
  Connected:       "green",
  Disbursed:       "green",
  "Loan Application": "blue",
  "Call Later":    "blue",
  "Loan application": "blue",
  Callback:        "amber",
  "Follow Up":     "amber",
  "No Answer":     "grey",
  "Not Connected": "grey",
  Ringing:         "grey",
  Busy:            "purple",
  "Wrong Number":  "red",
  "Not Interested": "red",
  Rejected:        "red",
  Called:          "grey",
  "Switched Off":  "grey",
};
function toneOf(dispo) { return DISPO_TONE[dispo] || "grey"; }

function CallingHistoryModal({ onClose }) {
  const [rows, setRows] = useState(null);
  useEffect(() => {
    http.get("/sa/call-history?days=7")
      .then((r) => setRows(r.data.history || []))
      .catch((err) => notify.error(err, "Failed to load calling history"));
  }, []);
  return (
    <Modal title="Calling History" onClose={onClose} wide>
      <p className="muted" style={{ margin: "0 0 12px", fontSize: 12 }}>
        Last 7 days · {rows?.length ?? 0} calls
      </p>
      {rows === null ? <Loading /> : rows.length === 0 ? (
        <div className="empty" style={{ padding: 24 }} data-testid="history-empty">
          No calls logged in the last 7 days.
        </div>
      ) : (
        <ul className="history-list" data-testid="history-list">
          {rows.map((r, i) => {
            const tone = toneOf(r.disposition);
            return (
              <li key={i} className={`history-row tone-${tone}`} data-testid={`history-row-${i}`}>
                <span className={`history-dot tone-${tone}`} aria-hidden />
                <div className="history-body">
                  <strong>{r.customer_name}</strong>
                  <span className="history-mobile">{r.mobile || "—"}</span>
                  {r.notes && <span className="history-notes">{r.notes}</span>}
                </div>
                <div className="history-meta">
                  <span className={`mc-tag ${tone}`}>{r.disposition}</span>
                  <span className="history-time">
                    {(r.created_at || "").slice(0, 16).replace("T", " ")}
                  </span>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </Modal>
  );
}
/*  and, after save, attaches the new lead to the campaign_customer   */
/*  (auto-marks as 'sent_to_login' and drops from the SA queue).      */
/* ------------------------------------------------------------------ */
function PrefilledLeadForm({ customer, user, onClose, onDone }) {
  const seed = useMemo(() => {
    // If the SA previously abandoned this Create Lead form (skip-draft), the
    // customer document already carries the last-typed values under
    // `skip_draft`. Merge them on top so the SA resumes exactly where they
    // left off. Reuse of the existing Skipped bucket — no new storage.
    const draft = customer?.skip_draft || {};
    return {
      customer_name: draft.customer_name ?? customer?.customer_name ?? "",
      mobile: draft.mobile ?? customer?.mobile ?? "",
      email: draft.email ?? customer?.email ?? "",
      pan: draft.pan ?? customer?.pan ?? "",
      address: draft.address ?? customer?.address ?? "",
      address_type: draft.address_type ?? customer?.address_type ?? "",
      city: draft.city ?? customer?.city ?? "",
      state: draft.state ?? customer?.state ?? "",
      pincode: draft.pincode ?? customer?.pincode ?? "",
      employment_type: draft.employment_type ?? customer?.employment_type ?? "Salaried",
      company: draft.company ?? customer?.company ?? "",
      designation: draft.designation ?? customer?.designation ?? "",
      monthly_income: draft.monthly_income ?? customer?.monthly_income ?? 0,
      existing_pos: draft.existing_pos ?? customer?.existing_pos ?? 0,
      existing_emi: draft.existing_emi ?? customer?.existing_emi ?? 0,
      loan_type: draft.loan_type ?? customer?.loan_type ?? "Fresh",
      loan_amount: draft.loan_amount ?? customer?.loan_amount ?? 0,
      loan_purpose: draft.loan_purpose ?? customer?.loan_purpose ?? "",
      other_information: draft.other_information ?? (customer?.other_information ||
        (customer?.extra ? Object.entries(customer.extra).map(([k, v]) => `${k}: ${v}`).join(" · ") : "")),
      campaign_id: draft.campaign_id ?? customer?.campaign_id ?? "",
      tenure_months: draft.tenure_months ?? customer?.tenure_months ?? "",
      // Bank / Product: prefer draft, else always blank so the SA picks
      // deliberately before saving.
      bank_id: draft.bank_id || "", bank_name: draft.bank_name || "",
      product_id: draft.product_id || "", product_name: draft.product_name || "",
      status: "New",
    };
  }, [customer]);

  // Track whether a lead has been created so unmount doesn't mis-persist.
  const createdRef = useRef(false);

  // On close/unmount: if the SA typed anything but did NOT save, persist
  // that partial state via the existing Skipped bucket so it shows up
  // under Calling Data → Skipped Lead and can be resumed later.
  useEffect(() => {
    return () => {
      if (createdRef.current) return; // lead was saved — nothing to skip
      const cid = customer?.customer_id;
      if (!cid) return;
      // Read the localStorage draft that LeadForm keeps for this user+customer.
      let draft = null;
      try {
        const key = `lead-draft::${user?.id || "anon"}::${cid}`;
        const raw = localStorage.getItem(key);
        draft = raw ? JSON.parse(raw)?.form : null;
      } catch { /* ignore */ }
      if (!draft) return;
      // Only persist if the SA actually changed something vs the untouched seed.
      const changed = Object.keys(draft).some((k) => {
        const a = draft[k], b = seed[k];
        return (a ?? "") !== (b ?? "") && a !== 0 && a !== "";
      });
      if (!changed) return;
      // Fire-and-forget — we're unmounting, don't block the UI.
      http.post(`/sa/calling-data/${cid}/skip-draft`, { form: draft })
        .catch(() => { /* silent */ });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customer?.customer_id]);

  const handleSaved = async (leadResp) => {
    // LeadForm calls onSaved(newLead) with the created lead payload.
    // STRICT WORKFLOW: only LINK the lead here — do NOT promote to
    // sent_to_login. The SA must explicitly click "Send to Login" for
    // that transition to happen (via /attach-lead).
    const leadId = leadResp?.lead_id;
    if (leadId && customer?.customer_id) {
      createdRef.current = true;
      try {
        await http.post(`/sa/calling-data/${customer.customer_id}/link-lead`, { lead_id: leadId });
        // Clear any prior Skipped-Lead mark for this customer so it drops
        // off the Skipped Lead bucket now that a real lead exists.
        http.post(`/sa/calling-data/${customer.customer_id}/skip-draft`, { clear: true })
          .catch(() => { /* silent */ });
        notify.success("Lead created — review the details, then click 'Send to Login'");
      } catch (err) {
        notify.error(err, "Lead created but link failed");
      }
    }
    // Pass the leadId up so the calling workspace can auto-open the
    // Review/Edit screen.
    onDone?.(leadId);
  };

  return <LeadForm prefill={seed} onClose={onClose} onSaved={handleSaved} user={user} />;
}



/* ================================================================== */
/*  1b. Customer call modal — reveal, log, callback, follow-up, Lead  */
/* ================================================================== */
export function CustomerCallModal({ customerId, onClose, onChanged, onCreateLead, onNext, autoReveal = false }) {
  const [data, setData] = useState(null);
  const [reveal, setReveal] = useState(autoReveal);
  const [dispoOptions, setDispoOptions] = useState(FALLBACK_DISPOSITIONS);
  const [form, setForm] = useState({
    disposition: "", notes: "",     // start empty — SA must pick a dispo
    is_interested: null,
    callback_at: "", followup_at: "",
  });
  const [saving, setSaving] = useState(false);
  const [savedDispo, setSavedDispo] = useState(false); // flips true only after Save succeeds

  const load = () => {
    http.get(`/sa/calling-data/${customerId}`)
      .then((r) => {
        setData(r.data);
      })
      .catch((err) => { notify.error(err, "Failed to load customer"); onClose(); });
  };
  useEffect(load, [customerId]);

  useEffect(() => {
    // Load configured dispositions from Admin master; fall back on failure.
    // Strict workflow: hide "Send to Bank" from the SA disposition grid — the
    // SA reaches the Bank via "Create Lead" only.
    http.get("/statuses?kind=disposition")
      .then((r) => {
        const labels = (r.data || [])
          .map((s) => s.label)
          .filter((lbl) => lbl && !/send to bank/i.test(lbl));
        if (labels.length) setDispoOptions(labels);
      })
      .catch(() => {/* keep fallback */});
  }, []);

  const c = data?.customer;
  const rawMobile = c?.mobile;
  const isSentToLogin = c?.status === "sent_to_login";
  const hasLinkedLead = !!c?.linked_lead_id && !isSentToLogin;

  const sendToLogin = async () => {
    if (!c?.linked_lead_id || isSentToLogin) return;
    setSaving(true);
    try {
      await http.post(`/sa/calling-data/${customerId}/attach-lead`,
        { lead_id: c.linked_lead_id });
      notify.success("Customer sent to Login");
      onChanged?.();
      onClose?.();
    } catch (err) {
      notify.error(err, "Failed to send to login");
      setSaving(false);
    }
  };

  const submit = async (chosenDispo) => {
    const dispo = chosenDispo || form.disposition;
    if (!dispo) {
      notify.error("Please select a disposition before saving.");
      return;
    }
    // "Create Lead" → open the pre-filled Lead form directly.
    // "Interested" / "Follow-up" → log disposition + add to My Follow-ups
    //   (backend inserts a followups row when followup_at is provided;
    //    it auto-defaults if the SA didn't pick a specific time).
    // STRICT WORKFLOW: do NOT auto-open Create Lead on Interested — the
    // customer moves into "My Follow-ups" so the SA can follow up for docs
    // before deciding to Create Lead.
    if (isCreateLeadDispo(dispo)) {
      if (isSentToLogin) {
        notify.success("Already promoted — lead " + c.sent_to_login_lead_id);
        onClose?.();
        return;
      }
      onCreateLead?.({ ...c });
      onClose?.();
      return;
    }
    if (saving || savedDispo) return; // idempotency guard against double-Save
    setSaving(true);
    try {
      await http.post(`/sa/calling-data/${customerId}/disposition`, {
        disposition: dispo,
        notes: form.notes,
        is_interested: form.is_interested,
        callback_at: form.callback_at,
        followup_at: form.followup_at,
      });
      notify.success(`Saved: ${dispo}`);
      setSavedDispo(true); // unlocks "Next Call"
      onChanged?.();
    } catch (err) { notify.error(err, "Failed to save disposition"); }
    setSaving(false);
  };

  const openCreateLead = () => {
    // Bubble the (already-loaded) customer up to the parent so it can
    // open the pre-filled LeadForm modal in a 2-column layout.
    if (c?.status === "sent_to_login") return;
    onCreateLead?.({ ...c });
  };

  const markInterested = () => {
    // Marking Interested is the trigger for Create Lead — open the pre-filled form.
    const nextInterested = form.is_interested === true ? null : true;
    setForm({ ...form, is_interested: nextInterested });
    if (nextInterested === true && c?.status !== "sent_to_login") {
      openCreateLead();
    }
  };

  if (!data) {
    return (
      <Modal onClose={onClose} title="Loading…">
        <Loading />
      </Modal>
    );
  }

  return (
    <Modal onClose={onClose} title={c?.customer_name || "Customer"} data-testid="call-modal" wide>
      <div className="call-modal-grid">
        <div>
          <p className="eyebrow">CUSTOMER</p>
          <div className="cust-info-grid" data-testid="cust-info-grid">
            <div className="cust-cell">
              <div className="cust-label">Name</div>
              <div className="cust-value">{c?.customer_name || "—"}</div>
            </div>
            <div className="cust-cell">
              <div className="cust-label">Mobile</div>
              <div className="cust-value">
                {reveal ? (
                  <a
                    href={`tel:${(rawMobile || "").replace(/\s+/g, "")}`}
                    style={{ fontFamily: "monospace", fontWeight: 700, textDecoration: "none" }}
                    data-testid="modal-mobile"
                  >
                    {rawMobile}
                  </a>
                ) : (
                  <span style={{ fontFamily: "monospace" }} data-testid="modal-mobile">
                    {maskMobile(rawMobile)}
                  </span>
                )}
                <button
                  className="cust-reveal-btn"
                  onClick={() => setReveal((v) => !v)}
                  data-testid="reveal-btn"
                  aria-label={reveal ? "Hide" : "Reveal"}
                >
                  {reveal ? <EyeOff size={13} /> : <Eye size={13} />}
                </button>
              </div>
            </div>
            <div className="cust-cell">
              <div className="cust-label">Location</div>
              <div className="cust-value">
                {c?.city || "—"}{c?.state ? `, ${c.state}` : ""}
              </div>
            </div>
            <div className="cust-cell">
              <div className="cust-label">Income</div>
              <div className="cust-value">{c?.monthly_income ? money(c.monthly_income) : "—"}</div>
            </div>
            <div className="cust-cell">
              <div className="cust-label">Existing POS</div>
              <div className="cust-value">
                {c?.existing_pos ? money(c.existing_pos) : "—"}
              </div>
            </div>
            <div className="cust-cell">
              <div className="cust-label">EMI</div>
              <div className="cust-value">{c?.existing_emi ? money(c.existing_emi) : "—"}</div>
            </div>
            <div className="cust-cell cust-cell-full">
              <div className="cust-label">Campaign ID</div>
              <div className="cust-value mono">{c?.campaign_id || "—"}</div>
            </div>
            <div className="cust-cell">
              <div className="cust-label">ROI</div>
              <div className="cust-value">
                {c?.roi != null && c?.roi !== "" ? `${c.roi}%` : "—"}
              </div>
            </div>
            <div className="cust-cell">
              <div className="cust-label">Loan</div>
              <div className="cust-value">
                {c?.loan_type || "—"}
                {c?.loan_amount ? <div className="cell-sub">{money(c.loan_amount)}</div> : null}
              </div>
            </div>
            <div className="cust-cell cust-cell-full">
              <div className="cust-label">Other Information</div>
              <div className="cust-value">
                {(() => {
                  const extras = c?.extra || {};
                  const keys = Object.keys(extras).filter(
                    (k) => extras[k] !== "" && extras[k] != null,
                  );
                  const misc = [];
                  if (c?.company) misc.push(["Company", c.company]);
                  if (c?.designation) misc.push(["Designation", c.designation]);
                  if (c?.pan) misc.push(["PAN", c.pan]);
                  if (c?.employment_type) misc.push(["Employment", c.employment_type]);
                  if (c?.tenure_months) misc.push(["Tenure", `${c.tenure_months} mo`]);
                  if (c?.loan_purpose) misc.push(["Purpose", c.loan_purpose]);
                  if (c?.bank_name) misc.push(["Ref bank", c.bank_name]);
                  keys.forEach((k) => misc.push([k, String(extras[k])]));
                  if (!misc.length) return <span className="muted">—</span>;
                  return (
                    <ul className="cust-extra-list" data-testid="cust-extra-list">
                      {misc.map(([k, v]) => (
                        <li key={k}><span className="muted">{k}</span>: {v}</li>
                      ))}
                    </ul>
                  );
                })()}
              </div>
            </div>
          </div>

          <p className="eyebrow" style={{ marginTop: 14 }}>CALL HISTORY</p>
          {data.dispositions?.length ? (
            <ul style={{ paddingLeft: 16, fontSize: 13 }}>
              {data.dispositions.map((d, i) => (
                <li key={i}>
                  <strong>{d.disposition}</strong>
                  <span className="cell-sub"> · {(d.created_at || "").slice(0, 16).replace("T", " ")}</span>
                  {d.notes ? <div className="cell-sub">{d.notes}</div> : null}
                </li>
              ))}
            </ul>
          ) : <div className="muted" style={{ fontSize: 13 }}>No calls logged yet.</div>}
        </div>

        <div>
          {!hasLinkedLead && !isSentToLogin && (
            <>
              <p className="eyebrow">LOG CALL</p>
              <label style={{ marginTop: 6, display: "block" }}>
                <div className="muted" style={{ fontSize: 12 }}>Notes (optional)</div>
                <textarea
                  data-testid="dispo-notes"
                  rows={2}
                  value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                />
              </label>
              <label style={{ marginTop: 8, display: "block" }}>
                <div className="muted" style={{ fontSize: 12 }}>
                  Follow-up date & time <span style={{ opacity: .7 }}>
                    (used when disposition is Interested or Follow-up — a reminder pops up at this time)
                  </span>
                </div>
                <input
                  type="datetime-local"
                  data-testid="followup-at"
                  value={form.followup_at}
                  onChange={(e) => setForm({ ...form, followup_at: e.target.value })}
                />
              </label>
              <details style={{ marginTop: 8 }}>
                <summary className="muted" style={{ fontSize: 12, cursor: "pointer" }}>
                  Advanced · pick a custom callback time
                </summary>
                <label style={{ display: "block", marginTop: 8 }}>
                  <div className="muted" style={{ fontSize: 12 }}>Callback at</div>
                  <input
                    type="datetime-local"
                    data-testid="callback-at"
                    value={form.callback_at}
                    onChange={(e) => setForm({ ...form, callback_at: e.target.value })}
                  />
                </label>
              </details>
            </>
          )}

          {isSentToLogin ? (
            <>
              <p className="eyebrow" style={{ marginTop: 14 }}>STATUS</p>
              <div
                data-testid="dispo-locked-banner"
                style={{
                  padding: 12, border: "1px solid #cbd5e1", borderRadius: 6,
                  background: "#f1f5f9", fontSize: 13, color: "#475569",
                }}
              >
                This customer has been <strong>Sent to Login</strong>
                {c?.sent_to_login_lead_id ? <> — lead <span className="mono">{c.sent_to_login_lead_id}</span></> : null}.
                Dispositions are locked.
              </div>
            </>
          ) : hasLinkedLead ? (
            <>
              <p className="eyebrow" style={{ marginTop: 14 }}>NEXT ACTION</p>
              <div
                data-testid="lead-created-banner"
                style={{
                  padding: 12, border: "1px solid #cbd5e1", borderRadius: 6,
                  background: "#ecfdf5", fontSize: 13, color: "#065f46",
                  marginBottom: 10,
                }}
              >
                Lead <span className="mono">{c.linked_lead_id}</span> created.
                Click <strong>Send to Login</strong> to submit it for processing.
                Dispositions are no longer available for this customer.
              </div>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                <button
                  className="primary-button compact"
                  onClick={sendToLogin}
                  disabled={saving}
                  data-testid="send-to-login-btn"
                  title="Move this customer to Sent to Login"
                  style={{ display: "inline-flex", alignItems: "center", gap: 6 }}
                >
                  <Send size={13} /> Send to Login
                </button>
              </div>
            </>
          ) : (
            <>
              <p className="eyebrow" style={{ marginTop: 14 }}>SELECT DISPOSITION</p>
              <div className="dispo-chips" data-testid="dispo-chip-grid">
                {dispoOptions.map((d) => {
                  const isSelected = form.disposition === d;
                  return (
                    <button
                      key={d}
                      type="button"
                      className={`dispo-chip${isSelected ? " dispo-chip-active" : ""}`}
                      data-testid={`dispo-chip-${d.replace(/\s+/g, "-").toLowerCase()}`}
                      // Clicking a chip only SELECTS it. Nothing is written to
                      // the DB until the SA hits the explicit Save button.
                      // Once saved, the row is locked so the SA can't switch
                      // dispositions without unlocking.
                      disabled={savedDispo}
                      onClick={() => setForm({ ...form, disposition: d })}
                    >
                      {d}
                    </button>
                  );
                })}
              </div>
              <div className="dispo-actions" data-testid="dispo-action-row">
                <button
                  type="button"
                  className="secondary-button compact"
                  onClick={openCreateLead}
                  data-testid="open-stl"
                  disabled={saving}
                >
                  <UserPlus size={13} /> Create Lead
                </button>
                <button
                  type="button"
                  className="primary-button compact"
                  data-testid="dispo-save-btn"
                  disabled={saving || !form.disposition || savedDispo}
                  onClick={() => submit()}
                  title={!form.disposition
                    ? "Select a disposition first"
                    : savedDispo ? "Already saved" : "Save disposition"}
                >
                  {saving ? <Loader2 size={12} className="spin" /> : null}
                  {savedDispo ? " Saved ✓" : " Save Disposition"}
                </button>
                <button
                  type="button"
                  className="primary-button compact"
                  data-testid="dispo-next-call-btn"
                  disabled={!savedDispo || !onNext}
                  onClick={() => { onClose?.(); onNext?.(customerId); }}
                  title={!savedDispo
                    ? "Please complete and save the disposition before the next call."
                    : "Open next customer in your queue"}
                >
                  Next Call →
                </button>
              </div>
              {!savedDispo && (
                <div
                  className="muted"
                  data-testid="dispo-guard-msg"
                  style={{ fontSize: 12, marginTop: 8 }}
                >
                  Please complete and save the disposition before the next call.
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </Modal>
  );
}


/* ================================================================== */
/*  2. MY FOLLOWUPS TAB                                                */
/* ================================================================== */
export function MyFollowupsTab({ onOpenLead }) {
  const [bucket, setBucket] = useState("all");
  const [rows, setRows] = useState(null);
  const [counts, setCounts] = useState({ all: 0, today: 0, upcoming: 0, overdue: 0, completed: 0 });
  const [selected, setSelected] = useState(null);
  const [leadPrefill, setLeadPrefill] = useState(null);
  const loadCounts = () => {
    http.get("/sa/followups/counts")
      .then((r) => setCounts(r.data || {}))
      .catch(() => { /* silent — chips just show 0 */ });
  };
  const reload = () => {
    setRows(null);
    http.get(`/sa/followups?bucket=${bucket}`)
      .then((r) => setRows(r.data.followups || []))
      .catch((err) => notify.error(err, "Failed to load follow-ups"));
    loadCounts();
  };
  useEffect(reload, [bucket]);

  const CHIPS = [
    { key: "all",       label: "All",       color: "#4f46e5" }, // indigo
    { key: "today",     label: "Today",     color: "#0284c7" }, // sky
    { key: "upcoming",  label: "Upcoming",  color: "#059669" }, // emerald
    { key: "overdue",   label: "Overdue",   color: "#dc2626" }, // red
    { key: "completed", label: "Completed", color: "#64748b" }, // slate
  ];

  return (
    <section className="panel" data-testid="my-followups-tab">
      <div className="section-title">
        <h3>My Follow-ups</h3>
      </div>
      <div className="fu-chip-strip" data-testid="fu-chip-strip">
        {CHIPS.map(({ key, label, color }) => {
          const active = bucket === key;
          return (
            <button
              key={key}
              type="button"
              className={`fu-chip ${active ? "fu-chip-active" : ""}`}
              onClick={() => setBucket(key)}
              data-testid={`fu-bucket-${key}`}
              style={{
                "--fu-color": color,
                background: active ? color : "#fff",
                color: active ? "#fff" : color,
                borderColor: color,
              }}
            >
              <span className="fu-chip-label">{label}</span>
              <span
                className="fu-chip-count"
                style={{
                  background: active ? "rgba(255,255,255,0.22)" : color,
                  color: "#fff",
                }}
              >{counts[key] ?? 0}</span>
            </button>
          );
        })}
      </div>
      {rows === null ? <Loading /> : rows.length === 0 ? (
        <div className="empty" style={{ padding: 24 }}>No follow-ups in this bucket.</div>
      ) : (
        <>
          {/* Desktop */}
          <div className="table-wrap desktop-list-only">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Due</th>
                  <th>Customer</th>
                  <th>Mobile</th>
                  <th>Loan</th>
                  <th>Notes</th>
                  <th>Status</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {rows.map((f, i) => (
                  <tr
                    key={i}
                    data-testid={`fu-row-${f.customer_id || f.lead_id || i}`}
                    onClick={() => {
                      if (f.lead_id && onOpenLead) onOpenLead(f.lead_id);
                      else if (f.customer_id) setSelected(f.customer_id);
                    }}
                    style={{ cursor: "pointer" }}
                  >
                    <td data-label="Due">{(f.due_at || "").slice(0, 16).replace("T", " ")}</td>
                    <td data-label="Customer">{f.customer_name || f.customer_id || f.lead_id}</td>
                    <td data-label="Mobile">{f.mobile || "—"}</td>
                    <td data-label="Loan">{f.loan_type}{f.loan_amount ? ` · ${money(f.loan_amount)}` : ""}</td>
                    <td data-label="Notes" className="cell-sub">{f.notes}</td>
                    <td data-label="Status"><span className={`pill pill-${f.status}`}>{f.status}</span></td>
                    <td onClick={(e) => e.stopPropagation()}>
                      {f.customer_id && (
                        <button
                          className="primary-button compact"
                          data-testid={`fu-open-call-${f.customer_id}`}
                          onClick={() => setSelected(f.customer_id)}
                        >
                          <PhoneCall size={13} /> Call
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile card list */}
          <ul className="mobile-card-list mobile-list-only" data-testid="followups-cards">
            {rows.map((f, i) => {
              const overdue = f.status === "pending" && f.due_at && new Date(f.due_at) < new Date();
              const dueLabel = (f.due_at || "").slice(0, 16).replace("T", " ");
              const tag = f.is_interested === true
                ? { text: "Interested", tone: "green" }
                : f.lead_status
                  ? { text: f.lead_status, tone: f.lead_status === "Rejected" ? "red" : "blue" }
                  : null;
              return (
                <li
                  key={i}
                  className="mc-row"
                  data-testid={`fu-card-${f.customer_id || f.lead_id || i}`}
                  onClick={() => {
                    // Same behaviour as other Calling Data cards: tapping the
                    // row body opens the full customer detail. Prefer the
                    // Lead sheet when a lead already exists; otherwise open
                    // the CustomerCallModal (which shows all customer info,
                    // call history, disposition + Create Lead).
                    if (f.lead_id && onOpenLead) onOpenLead(f.lead_id);
                    else if (f.customer_id) setSelected(f.customer_id);
                  }}
                  style={{ cursor: "pointer" }}
                >
                  <span className="mc-avatar">{initialsOf(f.customer_name || f.customer_id || f.lead_id)}</span>
                  <div className="mc-body">
                    <strong>{f.customer_name || f.customer_id || f.lead_id}</strong>
                    <span className="mc-mobile">{f.mobile || "—"}</span>
                    <span className="mc-loan">
                      {f.loan_type}
                      {f.loan_amount ? ` · ${money(f.loan_amount)}` : ""}
                    </span>
                    {tag && <span className={`mc-tag ${tag.tone}`}>{tag.text}</span>}
                    {overdue && (
                      <span className="mc-overdue">
                        <AlertTriangle size={12} /> {dueLabel} · Overdue
                      </span>
                    )}
                    {!overdue && dueLabel && (
                      <span className="mc-loan" style={{ marginTop: 4 }}>
                        Due {dueLabel}
                      </span>
                    )}
                  </div>
                  {f.customer_id ? (
                    <button
                      className="mc-call"
                      data-testid={`fu-open-call-${f.customer_id}`}
                      onClick={(e) => { e.stopPropagation(); setSelected(f.customer_id); }}
                    >
                      <PhoneCall size={14} /> Call
                    </button>
                  ) : (
                    <button className="mc-call disabled" disabled onClick={(e) => e.stopPropagation()}>
                      <PhoneCall size={14} /> Call
                    </button>
                  )}
                </li>
              );
            })}
          </ul>
        </>
      )}

      {selected && (
        <CustomerCallModal
          customerId={selected}
          onClose={() => setSelected(null)}
          onChanged={reload}
          onCreateLead={(cc) => { setSelected(null); setLeadPrefill(cc); }}
          autoReveal
        />
      )}
      {leadPrefill && (
        <PrefilledLeadForm
          customer={leadPrefill}
          onClose={() => setLeadPrefill(null)}
          onDone={(createdLeadId) => {
            setLeadPrefill(null);
            reload();
            if (createdLeadId && onOpenLead) onOpenLead(createdLeadId);
          }}
        />
      )}
    </section>
  );
}


/* ================================================================== */
/*  3. MANAGER TEAM DASHBOARD                                          */
/* ================================================================== */
export function ManagerTeamDashboard() {
  const [d, setD] = useState(null);
  useEffect(() => {
    http.get("/manager/team-funnel")
      .then((r) => setD(r.data))
      .catch((err) => notify.error(err, "Failed to load team dashboard"));
  }, []);
  if (!d) return <Loading />;

  const funnelStages = ["assigned", "called", "connected", "interested",
                         "followup_pending", "sent_to_login", "approved", "disbursed"];
  const stageLabels = {
    assigned: "Assigned", called: "Called", connected: "Connected",
    interested: "Interested", followup_pending: "Follow-up",
    sent_to_login: "Login", approved: "Approved", disbursed: "Disbursed",
  };

  return (
    <section className="panel" data-testid="manager-team-dashboard">
      <div className="section-title">
        <h3>Team Performance</h3>
        <span className="muted">{d.sa_count} sales associates</span>
      </div>

      <div style={{ display: "grid", gap: 10, gridTemplateColumns: `repeat(${funnelStages.length}, 1fr)`, marginTop: 12 }}>
        {funnelStages.map((s) => (
          <div key={s} className="stat-card" data-testid={`funnel-${s}`}>
            <div className="stat-label">{stageLabels[s]}</div>
            <div className="stat-value">{d.totals[s] || 0}</div>
          </div>
        ))}
      </div>

      <div className="table-wrap" style={{ marginTop: 20 }}>
        <table className="data-table" data-testid="team-funnel-table">
          <thead>
            <tr>
              <th>Sales Associate</th>
              <th>Assigned</th><th>Called</th><th>Connected</th>
              <th>Not Connected</th><th>Interested</th>
              <th>Not Interested</th><th>Follow-up</th>
              <th>Pending</th><th>Login</th><th>Approved</th><th>Disbursed</th>
            </tr>
          </thead>
          <tbody>
            {d.sas?.length ? d.sas.map((s) => (
              <tr key={s.sa_employee_id} data-testid={`sa-row-${s.sa_employee_id}`}>
                <td><strong>{s.sa_name}</strong><div className="cell-sub">{s.sa_email}</div></td>
                <td>{s.assigned}</td>
                <td>{s.called}</td>
                <td>{s.connected}</td>
                <td>{s.not_connected}</td>
                <td>{s.interested}</td>
                <td>{s.not_interested}</td>
                <td>{s.followup_pending}</td>
                <td>{s.pending}</td>
                <td>{s.sent_to_login}</td>
                <td>{s.approved}</td>
                <td>{s.disbursed}</td>
              </tr>
            )) : (
              <tr><td colSpan={12} className="muted" style={{ textAlign: "center", padding: 20 }}>
                No sales associates on this team yet.
              </td></tr>
            )}
          </tbody>
        </table>
      </div>
    </section>
  );
}


/* ================================================================== */
/*  4. PERFORMANCE REPORTS (5 report tabs)                             */
/* ================================================================== */
const reportTabs = [
  { id: "calling", label: "Calling" },
  { id: "followup", label: "Follow-up" },
  { id: "login", label: "Login" },
  { id: "approval", label: "Approval" },
  { id: "disbursement", label: "Disbursement" },
];

// Fixed 4×4 head arrangement requested by the ops team.
// null = empty tile ("empty for now").
const PERF_HEADS = [
  // Row 1
  { key: "assigned",       label: "Assigned",       source: "calling",       field: "assigned" },
  { key: "called",         label: "Called",         source: "calling",       field: "called" },
  { key: "interested",     label: "Interested",     source: "calling",       field: "interested" },
  { key: "not_interested", label: "Not Interested", source: "calling",       field: "not_interested" },
  // Row 2
  { key: "call_back",      label: "Call Back",      source: "overview",      field: "callback" },
  { key: "followups",      label: "Follow-ups",     source: "calling",       field: "followup_pending" },
  { key: "send_to_login",  label: "Send to Login",  source: "login",         field: "total_send_to_login" },
  { key: "login",          label: "Login",          source: "login",         field: "login_completed" },
  // Row 3
  { key: "approved",       label: "Approved",       source: "approval",      field: "approved" },
  { key: "disbursement",   label: "Disbursement",   source: "disbursement",  field: "count" },
  { key: "reject",         label: "Reject",         source: "approval",      field: "rejected" },
  null,
  // Row 4 — reserved / empty for now.
  null, null, null, null,
];

export function PerformanceReports() {
  // Default to TODAY so the report loads for the current date automatically.
  const todayIso = new Date().toISOString().slice(0, 10);
  const [tab, setTab] = useState("calling");
  const [d, setD] = useState(null);
  const [summary, setSummary] = useState(null);
  const [filters, setFilters] = useState({ date_from: todayIso, date_to: todayIso });

  const loadSummary = () => {
    const from = filters.date_from || todayIso;
    const to = filters.date_to || todayIso;
    const q = `?date_from=${from}&date_to=${to}`;
    Promise.all([
      http.get(`/reports/calling${q}`).catch(() => ({ data: { totals: {} } })),
      http.get(`/reports/login${q}`).catch(() => ({ data: { totals: {} } })),
      http.get(`/reports/approval${q}`).catch(() => ({ data: { totals: {} } })),
      http.get(`/reports/disbursement${q}`).catch(() => ({ data: { totals: {} } })),
      http.get(`/sa/calling-overview`).catch(() => ({ data: {} })),
    ]).then(([c, l, a, dis, ov]) => {
      setSummary({
        calling:      c.data.totals || {},
        login:        l.data.totals || {},
        approval:     a.data.totals || {},
        disbursement: dis.data.totals || {},
        overview:     ov.data || {},
      });
    });
  };

  const load = () => {
    setD(null);
    const from = filters.date_from || todayIso;
    const to = filters.date_to || todayIso;
    const q = new URLSearchParams();
    q.set("date_from", from);
    q.set("date_to", to);
    http.get(`/reports/${tab}?${q}`)
      .then((r) => setD(r.data))
      .catch((err) => notify.error(err, "Failed to load report"));
  };
  useEffect(load, [tab]);
  useEffect(loadSummary, []);

  const onApply = () => { load(); loadSummary(); };

  return (
    <section className="panel" data-testid="performance-reports">
      <div className="section-title">
        <h3>Performance Reports</h3>
        <div className="report-tabs">
          {reportTabs.map((r) => (
            <button
              key={r.id}
              className={`chip ${tab === r.id ? "chip-active" : ""}`}
              onClick={() => setTab(r.id)}
              data-testid={`report-tab-${r.id}`}
            >{r.label}</button>
          ))}
        </div>
      </div>

      <div className="report-filters">
        <label><div className="muted" style={{ fontSize: 12 }}>From</div>
          <input type="date" value={filters.date_from} onChange={(e) => setFilters({ ...filters, date_from: e.target.value })} data-testid="report-date-from" /></label>
        <label><div className="muted" style={{ fontSize: 12 }}>To</div>
          <input type="date" value={filters.date_to} onChange={(e) => setFilters({ ...filters, date_to: e.target.value })} data-testid="report-date-to" /></label>
        <button className="primary-button compact" onClick={onApply} data-testid="report-apply">Apply</button>
      </div>

      {/* Fixed 4×4 head grid — populated from aggregated summary. */}
      <div className="perf-head-grid" data-testid="perf-head-grid">
        {PERF_HEADS.map((h, i) => {
          if (!h) {
            return <div key={`empty-${i}`} className="perf-head perf-head-empty" aria-hidden="true" />;
          }
          const bucket = summary?.[h.source] || {};
          const value = bucket[h.field];
          return (
            <div key={h.key} className="perf-head" data-testid={`perf-head-${h.key}`}>
              <div className="perf-head-label">{h.label}</div>
              <div className="perf-head-value">{summary == null ? "—" : (value ?? 0)}</div>
            </div>
          );
        })}
      </div>

      {!d ? <Loading /> : (
        <div className="table-wrap report-sa-wrap" style={{ marginTop: 16 }}>
          <table className="data-table report-sa-table" data-testid={`report-${tab}-table`}>
            <thead>
              <tr>
                <th>Sales Associate</th>
                {Object.keys(d.sa_wise?.[0] || {}).filter((k) => k !== "sa_employee_id" && k !== "sa_name" && k !== "team_id").map((k) => (
                  <th key={k}>{k.replace(/_/g, " ")}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {(d.sa_wise || []).map((row) => (
                <tr key={row.sa_employee_id}>
                  <td><strong>{row.sa_name || row.sa_employee_id}</strong></td>
                  {Object.keys(row).filter((k) => k !== "sa_employee_id" && k !== "sa_name" && k !== "team_id").map((k) => (
                    <td key={k}>{(k === "amount" && typeof row[k] === "number") ? money(row[k]) : String(row[k] ?? "")}</td>
                  ))}
                </tr>
              ))}
              {!(d.sa_wise || []).length && (
                <tr><td colSpan={12} className="muted" style={{ padding: 20, textAlign: "center" }}>No data in range.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
